import { useRef, useState } from "react";
import { ClipboardDocumentIcon } from "@heroicons/react/24/outline";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ProgressSpinner } from "primereact/progressspinner";
import Button from "../ui/Button";
import EmptyModal from "../modals/EmptyModal";
import ReceivingGroupInlineInput from "../forms/receiving/invoices/ReceivingGroupInlineInput";
import { formatUnitOfMeasure } from "../../shared/formatUnitOfMeasure";
import { formatCurrency } from "../../shared/utility";
import { isDeveloperModeEnabled } from "../../shared/developerMode";
import ScannableBarcode from "../forms/traceability/receivingBarcode/ScannableBarcode";

function formatBarcodeSerial(value) {
  if (value == null || value === "") {
    return "—";
  }
  return String(value);
}

function placeName(value) {
  if (value == null || String(value).trim() === "") {
    return "—";
  }
  return value;
}

function cartonBarcode(row) {
  return String(row?.barcode ?? "").trim();
}

function parseStockAmount(value) {
  if (value == null || value === "") return null;
  const amount = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : null;
}

function amountBody(row, unitOfMeasure) {
  const unit = formatUnitOfMeasure(row.unit_of_measure || unitOfMeasure).trim();
  return `${row.total_stock}${unit ? ` ${unit}` : ""}`;
}

function costBody(row) {
  return (
    <span className="tabular-nums font-medium text-[#0F172A]">
      {formatCurrency(row.total_cost)}
    </span>
  );
}

export default function InventoryRawMaterialSubTable(props) {
  const {
    groups,
    loading,
    zonesByKey,
    loadingZones,
    zoneKeyFor,
    onExpandLocation,
    lotsByKey,
    loadingLots,
    lotKeyFor,
    onExpandZone,
    cartonsByKey,
    loadingCartons,
    cartonKeyFor,
    onExpandLot,
    unitOfMeasure,
    onUpdateReceivingItemStock,
    onPromote,
    onBarcodeCopied,
  } = props;

  const [expandedLocations, setExpandedLocations] = useState(null);
  const unitLabel = formatUnitOfMeasure(unitOfMeasure).trim();
  const reasonRequestRef = useRef(null);
  const savingReasonRef = useRef(false);
  const [reason, setReason] = useState("");
  const [savingReason, setSavingReason] = useState(false);
  const [reasonModal, setReasonModal] = useState({
    open: false,
    productName: "",
    nextStock: null,
  });

  const copyBarcode = async (row) => {
    const barcode = cartonBarcode(row);
    if (!barcode) {
      return;
    }

    try {
      await navigator.clipboard.writeText(barcode);
      onBarcodeCopied?.();
    } catch {
      onBarcodeCopied?.(false);
    }
  };

  const finishReasonRequest = (result) => {
    const pending = reasonRequestRef.current;
    reasonRequestRef.current = null;
    savingReasonRef.current = false;
    setSavingReason(false);
    setReason("");
    setReasonModal({ open: false, productName: "", nextStock: null });
    pending?.resolve(result);
  };

  const commitStock = (row, nextStock) => {
    return new Promise((resolve) => {
      reasonRequestRef.current = { resolve, row, nextStock };
      setReason("");
      setReasonModal({
        open: true,
        productName: row.product_name || "carton",
        nextStock,
      });
    });
  };

  const confirmReason = async () => {
    const pending = reasonRequestRef.current;
    const trimmedReason = reason.trim();
    if (!pending || !trimmedReason || savingReasonRef.current) {
      return;
    }

    savingReasonRef.current = true;
    setSavingReason(true);
    const unitCost = parseStockAmount(pending.row.unit_cost) ?? 0;
    const newItemCost = (pending.nextStock * unitCost).toFixed(2);

    try {
      const success = await onUpdateReceivingItemStock(
        pending.row.id,
        pending.nextStock,
        pending.row.old_in_stock ?? pending.row.in_stock,
        pending.row.total_cost,
        newItemCost,
        trimmedReason,
        pending.row,
      );
      finishReasonRequest(Boolean(success));
    } catch {
      finishReasonRequest(false);
    }
  };

  const cancelReason = () => {
    if (savingReasonRef.current) {
      return;
    }
    finishReasonRequest("cancel");
  };

  const serialTemplate = (row) => {
    const serial = formatBarcodeSerial(row.barcode_serial);
    const barcode = cartonBarcode(row);

    return (
      <div>
        <div className="flex items-center gap-1.5">
          <span className="tabular-nums font-mono text-[#0F172A]">{serial}</span>
          {barcode ? (
            <button
              type="button"
              className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[#64748B] hover:bg-[#F1F5F9] hover:text-[#0F172A]"
              title="Copy barcode"
              aria-label={`Copy barcode ${barcode}`}
              onClick={() => copyBarcode(row)}
            >
              <ClipboardDocumentIcon className="h-4 w-4" aria-hidden="true" />
            </button>
          ) : null}
        </div>
        {isDeveloperModeEnabled() && barcode ? (
          <ScannableBarcode value={barcode} showValue={false} />
        ) : null}
      </div>
    );
  };

  const promoteActionTemplate = (row) => {
    const inStock = parseStockAmount(row.in_stock);
    if (inStock == null || inStock <= 0) {
      return null;
    }

    return (
      <Button
        type="button"
        variant="secondary"
        size="sm"
        onClick={() => onPromote?.(row.id)}
      >
        Promote to sellable stock
      </Button>
    );
  };

  if (loading) {
    return <ProgressSpinner style={{ width: "30px", height: "30px" }} />;
  }

  const cartonExpansion = (lot) => {
    const key = cartonKeyFor(lot);
    if (loadingCartons[key]) {
      return <ProgressSpinner style={{ width: "30px", height: "30px" }} />;
    }

    return (
      <div className="operational-embedded-table overflow-hidden rounded-lg border border-[#E2E8F0] bg-white">
        <DataTable
          value={cartonsByKey[key] ?? []}
          className="p-datatable-sm text-sm"
          dataKey="id"
          emptyMessage="No cartons in this lot."
        >
          <Column
            field="supplier_name"
            header="Supplier Name"
            style={{ minWidth: "10rem" }}
          />
          <Column
            field="invoice_number"
            header="Invoice Number"
            style={{ minWidth: "8rem" }}
          />
          <Column
            field="product_name"
            header="Product Name"
            style={{ minWidth: "12rem" }}
            bodyClassName="font-medium text-[#0F172A]"
          />
          <Column
            header="Serial"
            body={serialTemplate}
            style={{ minWidth: "12rem" }}
          />
          <Column
            header="Unit Cost"
            alignHeader="right"
            body={(row) => (
              <span className="tabular-nums">
                {formatCurrency(row.unit_cost)}
              </span>
            )}
            style={{ minWidth: "7rem", textAlign: "right" }}
            bodyClassName="text-right tabular-nums"
          />
          <Column
            header="Total Unit"
            alignHeader="right"
            body={(row) => (
              <ReceivingGroupInlineInput
                ariaLabel={`Total units for ${row.product_name || "carton"}`}
                value={parseStockAmount(row.in_stock)}
                suffix={unitLabel || undefined}
                commitOnBlur
                onCommit={(value) => commitStock(row, value)}
              />
            )}
            style={{ minWidth: "11rem", width: "11rem", textAlign: "right" }}
            bodyClassName="overflow-visible text-right tabular-nums"
          />
          <Column
            header="Total Cost"
            alignHeader="right"
            body={costBody}
            style={{ minWidth: "7rem", textAlign: "right" }}
            bodyClassName="text-right tabular-nums"
          />
          <Column
            header="Actions"
            body={promoteActionTemplate}
            style={{ minWidth: "12rem" }}
          />
        </DataTable>
      </div>
    );
  };

  const lotExpansion = (zone) => {
    const key = lotKeyFor(zone);
    if (loadingLots[key]) {
      return <ProgressSpinner style={{ width: "30px", height: "30px" }} />;
    }

    return (
      <LotTable
        lots={lotsByKey[key] ?? []}
        unitOfMeasure={unitOfMeasure}
        cartonExpansion={cartonExpansion}
        onExpandLot={onExpandLot}
      />
    );
  };

  const zoneExpansion = (location) => {
    const key = zoneKeyFor(location);
    if (loadingZones[key]) {
      return <ProgressSpinner style={{ width: "30px", height: "30px" }} />;
    }

    return (
      <ZoneTable
        zones={zonesByKey[key] ?? []}
        unitOfMeasure={unitOfMeasure}
        lotExpansion={lotExpansion}
        onExpandZone={onExpandZone}
      />
    );
  };

  return (
    <div className="operational-embedded-table overflow-hidden rounded-lg border border-[#E2E8F0] bg-white">
      <DataTable
        value={groups ?? []}
        expandedRows={expandedLocations}
        onRowToggle={(event) => setExpandedLocations(event.data)}
        onRowExpand={(event) => onExpandLocation(event.data)}
        rowExpansionTemplate={zoneExpansion}
        dataKey="group_key"
        className="p-datatable-sm text-sm"
        emptyMessage="No stock in the selected location or zone."
      >
        <Column
          header="Location"
          body={(row) => placeName(row.location_name)}
          style={{ minWidth: "10rem" }}
        />
        <Column
          header="Amount in stock"
          alignHeader="right"
          body={(row) => amountBody(row, unitOfMeasure)}
          style={{ minWidth: "10rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column
          header="Total Cost"
          alignHeader="right"
          body={costBody}
          style={{ minWidth: "8rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column expander={() => true} header="" style={{ width: "5rem" }} />
      </DataTable>
      <EmptyModal
        open={reasonModal.open}
        setOpen={(next) => {
          if (!next.open) {
            cancelReason();
          }
        }}
        childComponent={
          <form
            className="text-left"
            onSubmit={(event) => {
              event.preventDefault();
              void confirmReason();
            }}
          >
            <h3 className="text-base font-medium text-[#0F172A]">
              Reason for stock change
            </h3>
            <p className="mt-2 text-sm text-[#64748B]">
              {reasonModal.productName} will be updated to{" "}
              {reasonModal.nextStock}
              {unitLabel ? ` ${unitLabel}` : ""}. This reason is stored on the
              barcode history.
            </p>
            <label
              htmlFor="inventory-stock-reason"
              className="mt-4 block text-sm font-medium text-[#0F172A]"
            >
              Reason
            </label>
            <textarea
              id="inventory-stock-reason"
              value={reason}
              onChange={(event) => setReason(event.target.value)}
              rows={3}
              required
              autoFocus
              className="mt-1 block w-full rounded-xl border border-[#E2E8F0] bg-white px-3 py-2 text-sm text-[#0F172A] shadow-sm placeholder:text-[#94A3B8] focus:border-[#16A34A] focus:outline-none focus:ring-2 focus:ring-[#BBF7D0]"
              placeholder="Why is this quantity changing?"
            />
            <div className="mt-5 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="secondary"
                size="md"
                onClick={cancelReason}
                disabled={savingReason}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="primary"
                size="md"
                loading={savingReason}
                disabled={!reason.trim() || savingReason}
              >
                Save
              </Button>
            </div>
          </form>
        }
      />
    </div>
  );
}

function ZoneTable({ zones, unitOfMeasure, lotExpansion, onExpandZone }) {
  const [expandedZones, setExpandedZones] = useState(null);

  return (
    <div className="operational-embedded-table overflow-hidden rounded-lg border border-[#E2E8F0] bg-white">
      <DataTable
        value={zones}
        expandedRows={expandedZones}
        onRowToggle={(event) => setExpandedZones(event.data)}
        onRowExpand={(event) => onExpandZone(event.data)}
        rowExpansionTemplate={lotExpansion}
        dataKey="group_key"
        className="p-datatable-sm text-sm"
        emptyMessage="No zones in this location."
      >
        <Column
          header="Zone"
          body={(row) => placeName(row.zone_name)}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Amount in stock"
          alignHeader="right"
          body={(row) => amountBody(row, unitOfMeasure)}
          style={{ minWidth: "10rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column
          header="Total Cost"
          alignHeader="right"
          body={costBody}
          style={{ minWidth: "8rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column expander={() => true} header="" style={{ width: "5rem" }} />
      </DataTable>
    </div>
  );
}

function LotTable({ lots, unitOfMeasure, cartonExpansion, onExpandLot }) {
  const [expandedLots, setExpandedLots] = useState(null);

  return (
    <div className="operational-embedded-table overflow-hidden rounded-lg border border-[#E2E8F0] bg-white">
      <DataTable
        value={lots}
        expandedRows={expandedLots}
        onRowToggle={(event) => setExpandedLots(event.data)}
        onRowExpand={(event) => onExpandLot(event.data)}
        rowExpansionTemplate={cartonExpansion}
        dataKey="group_key"
        className="p-datatable-sm text-sm"
        emptyMessage="No lots in this zone."
      >
        <Column
          header="Lot"
          body={(row) => placeName(row.lot_number)}
          style={{ minWidth: "8rem" }}
        />
        <Column
          header="Amount in stock"
          alignHeader="right"
          body={(row) => amountBody(row, unitOfMeasure)}
          style={{ minWidth: "10rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column
          header="Total Cost"
          alignHeader="right"
          body={costBody}
          style={{ minWidth: "8rem", textAlign: "right" }}
          bodyClassName="text-right tabular-nums"
        />
        <Column expander={() => true} header="" style={{ width: "5rem" }} />
      </DataTable>
    </div>
  );
}
